import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiCopy, FiGrid, FiList } from 'react-icons/fi';
import { Images } from '../../../utils/imageConstants';
import BreadCrumb from '../../../components/BreadCrumb';
import NoDataFound from '../../../components/common/NoDataFound';
import useDebounce from '../../../hooks/useDebounce';
import { getAuthToken } from '../../../helper/helper';
import { useAppSelector } from '../../../redux/hooks';
import { selectSelectedLeagueId } from '../../../redux/slice/selectedLeagueSlice';
import { QUERY_KEY } from '../../../services/queryEndPoint';
import { fetchLeagueById } from '../../myLeagues/api/myLeaguesApi';
import { fetchDuplicatePlayerGroups, fetchLeaguePlayers } from './api/playersApi';
import PlayerRosterCard from './components/PlayerRosterCard';
import PlayerRosterListView from './components/PlayerRosterListView';
import Pagination from './components/Pagination';
import DuplicatePlayersBanner from './components/DuplicatePlayersBanner';
import DuplicatePlayersReviewModal from './components/DuplicatePlayersReviewModal';
import './Players.scss';

const PAGE_SIZE = 20;

const Players: React.FC = () => {
  const selectedLeagueId = useAppSelector(selectSelectedLeagueId);

  // Source of truth for canEditLeague is the dedicated GET league/{leagueId} endpoint, not
  // the selectedLeague Redux slice (populated from the bulk getAllLeagues list, which isn't
  // guaranteed to carry every permission flag). Same query key SubHeader.tsx/Tournaments.tsx
  // already use for this league, so it's typically served from cache, not a fresh request.
  const { data: leagueDetails } = useQuery({
    queryKey: [QUERY_KEY.getLeagueDetails, selectedLeagueId],
    enabled: !!selectedLeagueId,
    queryFn: () => fetchLeagueById(selectedLeagueId!),
  });
  const canManageDuplicates = !!getAuthToken() && Boolean(leagueDetails?.canEditLeague);

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [page, setPage] = useState(0);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');

  // Reset to page 0 whenever the search changes.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchQuery]);

  const {
    data: playersPage,
    isLoading: isLoadingPlayers,
    isError: isPlayersError,
  } = useQuery({
    queryKey: [QUERY_KEY.leaguePlayersSearch, selectedLeagueId, 'players-tab', debouncedSearchQuery, page],
    enabled: !!selectedLeagueId,
    queryFn: () =>
      fetchLeaguePlayers(selectedLeagueId!, {
        search: debouncedSearchQuery || undefined,
        page,
        size: PAGE_SIZE,
      }),
  });
  const players = playersPage?.content ?? [];

  const { data: duplicateGroups = [] } = useQuery({
    queryKey: [QUERY_KEY.leagueDuplicatePlayerGroups, selectedLeagueId],
    enabled: !!selectedLeagueId && canManageDuplicates,
    queryFn: () => fetchDuplicatePlayerGroups(selectedLeagueId!),
  });
  const totalDuplicateProfiles = useMemo(
    () => duplicateGroups.reduce((sum, g) => sum + (g.profileCount ?? 0), 0),
    [duplicateGroups]
  );

  return (
    <div className="main-middle-content-wrap">
      <div className="players-tab-wrapper custom-container">
        <BreadCrumb />

        {canManageDuplicates && (
          <DuplicatePlayersBanner
            totalProfileCount={totalDuplicateProfiles}
            onReviewClick={() => setReviewModalOpen(true)}
          />
        )}

        <div className="players-topbar">
          <h1 className="players-heading">Players</h1>
          <div className="players-filters">
            <div className="players-searchbox">
              <input
                type="text"
                placeholder="Search name, playercode..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <img src={Images.dark_search_icon} alt="Search" />
            </div>

            {canManageDuplicates && (
              <button
                type="button"
                className="players-duplicates-pill"
                onClick={() => setReviewModalOpen(true)}
              >
                <FiCopy aria-hidden />
                Duplicates
                <span className="players-duplicates-pill__count">{totalDuplicateProfiles}</span>
              </button>
            )}

            <div className="players-view-toggle" role="group" aria-label="Switch view">
              <button
                type="button"
                className={`players-view-toggle__btn ${viewMode === 'cards' ? 'active' : ''}`}
                onClick={() => setViewMode('cards')}
              >
                <FiGrid aria-hidden />
                Cards
              </button>
              <button
                type="button"
                className={`players-view-toggle__btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                <FiList aria-hidden />
                List
              </button>
            </div>
          </div>
        </div>

        {isLoadingPlayers ? (
          <div className="players-tab-loading">Loading...</div>
        ) : isPlayersError ? (
          <div className="players-tab-error" role="alert">
            Could not load players. Please try again.
          </div>
        ) : players.length === 0 ? (
          <NoDataFound message="NO PLAYERS FOUND" />
        ) : (
          <>
            {viewMode === 'cards' ? (
              <div className="player-roster-grid">
                {players.map((player) => (
                  <PlayerRosterCard key={player.playerId} player={player} />
                ))}
              </div>
            ) : (
              <PlayerRosterListView players={players} />
            )}
            {playersPage && (
              <Pagination
                pageNo={playersPage.pageNo}
                totalPages={playersPage.totalPages}
                totalElements={playersPage.totalElements}
                pageSize={PAGE_SIZE}
                hasNext={playersPage.hasNext}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>

      {canManageDuplicates && selectedLeagueId && (
        <DuplicatePlayersReviewModal
          leagueId={selectedLeagueId}
          open={reviewModalOpen}
          onOpenChange={setReviewModalOpen}
          groups={duplicateGroups}
          onMergeComplete={() => {
            // Keep the modal open so the admin can review remaining groups; the groups
            // query has already been invalidated by the modal itself on success.
          }}
        />
      )}
    </div>
  );
};

export default Players;
